import { useState, useEffect, useRef, useMemo } from 'react';
import { MOODASH_ROUTE } from '../../utils/endpoints';
import { ErrorToast } from '../../utils/toasters';
import { requests } from '../../utils';
import { IDiscoveryPreSetData, MoodashActivePlatforms } from '../../app/brand/campaign/discovery/dto/IDiscoveryFilter';
import { GlobalDiscoveryService } from '../../app/brand/global-discovery/service';
import { BaseModel } from '../../model/shared/BaseModel';

export interface IMoodashFilterState {
    platform: MoodashActivePlatforms;
    displayName: string;
    hashtags: string;
    topics: string;
    users: string;
    mentions: string;
    locations: string;
    audienceLocations: string;
    interests: string;
    languages: string;
}

const useMoodashData = () => {
    const [locations, setLocations] = useState([]);
    const [audienceLocations, setAudienceLocations] = useState([]);
    const [interests, setInterests] = useState([]);
    const [hashtags, setHashtags] = useState([]);
    const [users, setUsers] = useState([]);
    const [mentions, setMentions] = useState([]);
    const [topics, setTopics] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [presetOptions, setPresetOptions] = useState<BaseModel<IDiscoveryPreSetData[]>>();
    const [filterParams, setFilterParams] = useState<IMoodashFilterState>({
        platform: MoodashActivePlatforms.INSTAGRAM,
        displayName: '',
        hashtags: '',
        topics: '',
        users: '',
        mentions: '',
        locations: '',
        audienceLocations: '',
        interests: '',
        languages: '',
    });
    const [presetOptionFilter, setPresetOptionFilter] = useState({
        perPage: 10,
        page: 1,
    });

    const didMountRef = useRef({
        interests: false,
        locations: false,
        audienceLocations: false,
        hashtags: false,
        users: false,
        mentions: false,
        topics: false,
        languages: false,
    });

    const fetchMoodashResource = async (
        resource: string,
        destructuringParam: string,
        paramValue: string,
        setter: (data: any) => void
    ) => {
        try {
            const response = await requests.get(`${MOODASH_ROUTE}/${resource}`, {
                query: paramValue,
                platform: filterParams?.platform.toLowerCase(),
            });
            await setter(response[destructuringParam]);
        } catch (error) {
            ErrorToast(error);
        }
    };

    useEffect(() => {
        fetchMoodashResource('interests', 'interests', filterParams.interests, setInterests);
    }, [filterParams.interests]);

    useEffect(() => {
        if (didMountRef.current.locations) {
            fetchMoodashResource('locations', 'locations', filterParams.locations, setLocations);
        } else {
            didMountRef.current.locations = true;
        }
    }, [filterParams.locations]);

    useEffect(() => {
        if (didMountRef.current.audienceLocations) {
            fetchMoodashResource('locations', 'locations', filterParams.audienceLocations, setAudienceLocations);
        } else {
            didMountRef.current.audienceLocations = true;
        }
    }, [filterParams.audienceLocations]);

    useEffect(() => {
        if (didMountRef.current.hashtags) {
            fetchMoodashResource('hashtags', 'tags', filterParams.hashtags, setHashtags);
        } else {
            didMountRef.current.hashtags = true;
        }
    }, [filterParams.hashtags]);

    useEffect(() => {
        if (didMountRef.current.users) {
            fetchMoodashResource('users', 'users', filterParams.users, setUsers);
        } else {
            didMountRef.current.users = true;
        }
    }, [filterParams.users]);

    useEffect(() => {
        if (didMountRef.current.mentions) {
            fetchMoodashResource('users', 'users', filterParams.mentions, setMentions);
        } else {
            didMountRef.current.mentions = true;
        }
    }, [filterParams.mentions]);

    useEffect(() => {
        if (didMountRef.current.topics) {
            fetchMoodashResource('topics', 'tags', filterParams.topics, setTopics);
        } else {
            didMountRef.current.topics = true;
        }
    }, [filterParams.topics]);

    useEffect(() => {
        if (didMountRef.current.languages) {
            fetchMoodashResource('languages', 'languages', filterParams.languages, setLanguages);
        } else {
            didMountRef.current.languages = true;
        }
    }, [filterParams.languages]);

    useEffect(() => {
        const queryParams = new URLSearchParams({
            platformName: filterParams.platform ? filterParams.platform.toLowerCase() : '',
        }).toString();

        function getFilterPreset() {
            GlobalDiscoveryService.getPresetFilterData(presetOptionFilter, queryParams)
                .then(response => {
                    setPresetOptions(response);
                })
                .catch(error => ErrorToast(error));
        }
        getFilterPreset();
    }, [filterParams?.platform, presetOptionFilter]);

    const memoizedInterests = useMemo(() => interests, [interests]);
    const memoizedLocations = useMemo(() => locations, [locations]);
    const memoizedAudienceLocations = useMemo(() => audienceLocations, [audienceLocations]);
    const memoizedHashtags = useMemo(() => hashtags, [hashtags]);
    const memoizedUsers = useMemo(() => users, [users]);
    const memoizedMentions = useMemo(() => mentions, [mentions]);
    const memoizedTopics = useMemo(() => topics, [topics]);
    const memoizedLanguages = useMemo(() => languages, [languages]);
    const memoizedPresetOptions = useMemo(() => presetOptions, [presetOptions]);

    return {
        setFilterParams,
        filterParams,
        interests: memoizedInterests,
        locations: memoizedLocations,
        audienceLocations: memoizedAudienceLocations,
        topics: memoizedTopics,
        users: memoizedUsers,
        hashtags: memoizedHashtags,
        languages: memoizedLanguages,
        mentions: memoizedMentions,
        presetOptions: memoizedPresetOptions,
        setPresetOptionFilter,
        presetOptionFilter,
    };
};

export default useMoodashData;
